import Image from 'next/image'
import Link from 'next/link'
import { Fragment, useId } from 'react'
import clsx from 'clsx'
import { ArrowRight, Check } from 'lucide-react'
import BookButton from '@/components/ui/BookButton'
import HeroMesh from '@/components/hero-mesh'
import Subtitle from '@/components/ui/subtitle'
import seo from '@/public/pages/home/seo-morflate-2026.png'

const defaultSeoContent = {
  title: 'Search Engine Optimization',
  subtitle: 'SEO',
  description:
    'Our proprietary SEO strategies help potential customers find you. We’ll help you rank higher in search engine results pages to increase organic traffic and visibility.',
  list: [
    {
      title: 'Organic Traffic Growth',
      description: 'Maximizing visibility in search engines.',
    },
    {
      title: 'Local SEO Expertise',
      description: 'Specializing in geographically-targeted SEO strategies.',
    },
    {
      title: 'SEO Audits',
      description:
        'Conducting thorough examinations to identify optimization opportunities.',
    },
    {
      title: 'Custom SEO Plans',
      description:
        'Tailoring strategies to align with your business objectives.',
    },
  ],
  buttonText: 'Ecommerce SEO Services',
  link: '/seo',
  image: seo,
  imageAlt: 'SEO',
  imageSizes:
    '(min-width: 1380px) 549px, (min-width: 1100px) calc(32.69vw + 104px), (min-width: 520px) 442px, calc(94vw - 22px)',
  textColor: 'text-vblue3',
  gradientTextColor: 'from-vblue2 to-vcyan1',
  checkColor: 'text-vblue3',
  buttonColor: 'blue',
}

const SEO = ({
  content = defaultSeoContent,
  id,
  className = 'relative pt-16',
  meshEdgeOnly = false,
  meshScheme,
  reverse = true,
}) => {
  const generatedSectionId = useId()
  const sectionId = id ?? generatedSectionId
  const seoContent = {
    ...defaultSeoContent,
    ...content,
  }

  return (
    <section id={sectionId} className={className}>
      {meshScheme ? (
        <HeroMesh scheme={meshScheme} edgeOnly={meshEdgeOnly} />
      ) : null}
      <div className="relative mx-auto max-w-screen-xl px-5 md:px-10">
        <div
          className={clsx(
            'flex w-full items-center justify-between',
            seoContent.images
              ? 'flex-col gap-8'
              : clsx(
                  'flex-col-reverse gap-10 lg:gap-20',
                  reverse ? 'lg:flex-row-reverse' : 'lg:flex-row',
                ),
          )}
        >
          <div
            className={
              seoContent.images ? 'mx-auto max-w-3xl text-center' : 'lg:w-3/5'
            }
          >
            <Subtitle
              subtitle={seoContent.subtitle}
              textColor={seoContent.textColor}
              gradientTextColor={seoContent.gradientTextColor}
              center={Boolean(seoContent.images)}
            />
            <h2 className="mb-2.5 text-3xl font-bold tracking-tight lg:text-4xl">
              {seoContent.title}
            </h2>
            <p className="text-lg">{seoContent.description}</p>
            {!seoContent.images && (
              <>
                <ul className="my-5 mr-auto grid justify-start gap-2.5 lg:grid-cols-2">
                  {seoContent.list.map((item) => (
                    <li
                      key={item.title}
                      className="flex items-center gap-2.5 font-montserrat font-semibold"
                    >
                      {item.href ? (
                        <Link
                          href={item.href}
                          className="flex items-center gap-2.5 text-inherit no-underline underline-offset-4 hover:text-inherit hover:underline focus-visible:underline"
                        >
                          <Check
                            aria-hidden="true"
                            className={clsx(
                              seoContent.checkColor,
                              'size-5 shrink-0',
                            )}
                          />
                          {item.title}
                        </Link>
                      ) : (
                        <>
                          <Check
                            aria-hidden="true"
                            className={clsx(
                              seoContent.checkColor,
                              'size-5 shrink-0',
                            )}
                          />
                          {item.title}
                        </>
                      )}
                    </li>
                  ))}
                </ul>
                <div className="mt-8 flex">
                  <BookButton
                    color={seoContent.buttonColor}
                    text={seoContent.buttonText}
                    link={seoContent.link}
                    custom="!w-full px-5"
                    right
                    icon={<ArrowRight className="!size-5" />}
                  />
                </div>
              </>
            )}
          </div>
          <div
            className={
              seoContent.images
                ? 'w-full'
                : 'w-full max-w-md lg:block lg:w-2/5 lg:max-w-none'
            }
          >
            {seoContent.images ? (
              <div
                className={clsx(
                  'grid grid-cols-1 items-center gap-6',
                  seoContent.images.length > 1
                    ? 'sm:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)]'
                    : 'mx-auto max-w-4xl',
                )}
              >
                {seoContent.images.map((image, index) => (
                  <Fragment key={image.caption}>
                    {index > 0 && (
                      <ArrowRight
                        aria-hidden="true"
                        className="size-8 rotate-90 justify-self-center text-vblue3 sm:mt-8 sm:rotate-0"
                      />
                    )}
                    <figure className="min-w-0">
                      <figcaption className="mb-3 font-montserrat text-sm font-semibold text-black">
                        {image.caption}
                      </figcaption>
                      <Image
                        loading="lazy"
                        src={image.src}
                        alt={image.alt}
                        sizes={seoContent.imageSizes}
                        quality={85}
                        className={clsx(
                          'h-auto w-full object-cover',
                          seoContent.imageClassName,
                        )}
                      />
                    </figure>
                  </Fragment>
                ))}
              </div>
            ) : (
              <Image
                loading="lazy"
                src={seoContent.image}
                alt={seoContent.imageAlt}
                sizes={seoContent.imageSizes}
                quality={75}
                className={clsx(
                  'h-auto w-full object-cover',
                  seoContent.imageClassName,
                )}
              />
            )}
          </div>
        </div>
      </div>
    </section>
  )
}

export default SEO
